import { Field } from 'redux-form/immutable';
import React, { useState } from 'react';
import type { Map as ImmutableMap } from 'immutable';

import * as validators from 'utils/validators';
import PhoneNumber from 'retired/PhoneNumberField';
import countries from 'utils/countries';
import FormLocationField from 'retired/FormLocationField';
import FormTextField from 'retired/FormTextField';
import addressToLocation from 'utils/addressToLocation';
import createTextMask from 'components/MaskInput/createTextMask';

const minLength2 = validators.minLength(2);
const isAddress = validators.isAddress();
const isEmail = validators.isEmail();
const isPhone = validators.isPhone();
const isEin = validators.isEin();

const einMask = createTextMask({
  pattern: '99-9999999',
  stripMask: true,
});

type FormData = {
  organization?: string;
  partnerEmail?: string;
  partnerLocation?: string;
  partnerName?: string;
  status?: string;
};

const validateName = (value: string | undefined, allValues: ImmutableMap<FormData, string>) => {
  const formValues = allValues.toJS() as FormData;

  if ((!formValues.organization || formValues.partnerEmail) && !value) {
    return 'Required';
  }
  if (value) {
    return minLength2(value);
  }
  return undefined;
};

const validateEmail = (value: string | undefined, allValues: ImmutableMap<FormData, string>) => {
  const formValues = allValues.toJS() as FormData;

  if ((!formValues.organization || formValues.partnerName) && !value) {
    return 'Required';
  }
  if (value) {
    return isEmail(value);
  }
  return undefined;
};

export const NewPartner = (): JSX.Element => {
  const [locationObjectFromAddress, setLocationObjectFromAddress] = useState<Record<
    PropertyKey,
    unknown
  > | null>(null);

  const handleLocationSelect = (address: string) =>
    addressToLocation(address).subscribe(({ locationObject }) => {
      setLocationObjectFromAddress(locationObject);
    });

  return (
    <>
      <Field
        name="partnerName"
        component={FormTextField}
        fullWidth
        label="Nonprofit Name*"
        autoComplete="off"
        validate={validateName}
      />
      <Field
        name="partnerEmail"
        component={FormTextField}
        fullWidth
        label="Nonprofit Email*"
        autoComplete="off"
        validate={validateEmail}
      />
      <Field
        name="partnerLocation"
        component={FormLocationField}
        fullWidth
        label="Location"
        helperText="Please enter a full address"
        autoComplete="off"
        validate={isAddress}
        afterSelect={handleLocationSelect}
        sx={{ textTransform: 'uppercase' }}
      />
      {locationObjectFromAddress?.countryCode === countries.US.alpha2 ? (
        <Field
          id="partnerEin"
          name="partnerEin"
          component={FormTextField}
          fullWidth
          label="Nonprofit EIN"
          {...einMask}
          validate={isEin}
        />
      ) : null}
      <Field
        id="partnerPhone"
        name="partnerPhone"
        type="tel"
        component={PhoneNumber}
        fullWidth
        label="Phone Number"
        country={locationObjectFromAddress?.countryCode || 'US'}
        validate={isPhone}
        style={{ marginTop: 10 }}
      />
    </>
  );
};
